export type CanonicalRole =
  | 'SUPER_ADMIN'
  | 'ADMINISTRADOR'
  | 'DIRECTOR_NACIONAL'
  | 'VICEPRESIDENTE'
  | 'COORDINADOR'
  | 'PRESIDENTE'
  | 'EDITOR'
  | 'VISOR';

export interface RBACCapabilities {
  'users.view': boolean;
  'users.create': boolean;
  'users.edit': boolean;
  'users.assign_roles': boolean;
  'users.assign_scope': boolean;
  
  'exports.pdf': boolean;
  'exports.instance_data': boolean;
  'exports.full_excel': boolean;
  'exports.full_backup': boolean;
  
  'sync.view': boolean;
  'sync.execute': boolean;
  
  'settings.manage': boolean;
  'recovery.manage': boolean;
}

export type LegacyCapability = 
  | 'canManageUsers' 
  | 'canEditConfig' 
  | 'canDownloadReports' 
  | 'canUploadEvidence' 
  | 'canValidateEvidence' 
  | 'canDeleteRecords' 
  | 'canViewAllInstances' 
  | 'canManageOwnInstance';

export function isCanonicalRole(role: any): role is CanonicalRole {
  return [
    'SUPER_ADMIN', 'ADMINISTRADOR', 'DIRECTOR_NACIONAL', 
    'VICEPRESIDENTE', 'COORDINADOR', 'PRESIDENTE', 
    'EDITOR', 'VISOR'
  ].includes(role);
}

export function normalizeRole(rawRole: string | null | undefined): CanonicalRole {
  if (!rawRole) return 'VISOR';
  
  const upper = rawRole.toUpperCase().trim();
  
  switch (upper) {
    case 'SUPER_ADMIN':
    case 'SA':
      return 'SUPER_ADMIN';
      
    case 'ADMINISTRADOR':
    case 'ADMIN':
      return 'ADMINISTRADOR';
      
    case 'DIRECTOR_NACIONAL':
    case 'DIRECTOR_GRAL':
    case 'ND':
      return 'DIRECTOR_NACIONAL';
      
    case 'VICEPRESIDENTE':
    case 'VICE_PRESIDENT':
    case 'VP':
      return 'VICEPRESIDENTE';
      
    case 'COORDINADOR':
    case 'COORDINADORES':
      return 'COORDINADOR';
      
    case 'PRESIDENTE':
    case 'COMMISSION_PRESIDENT':
    case 'INSTANCIA_PRESIDENTE':
    case 'PRESIDENTE_INSTANCIA':
      return 'PRESIDENTE';
      
    case 'EDITOR':
    case 'CAPTURA':
    case 'CAPTURISTA':
    case 'COMMISSION_USER':
    case 'USER':
      return 'EDITOR';
      
    case 'VISOR':
    case 'VIEWER':
    case 'VIEW_ONLY':
    case 'LECTURA':
      return 'VISOR';
      
    default:
      return 'VISOR';
  }
}

export function getRoleCapabilities(role: CanonicalRole): RBACCapabilities {
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin = role === 'ADMINISTRADOR';
  const isDN = role === 'DIRECTOR_NACIONAL';
  const isAdminOrHigher = isSuperAdmin || isAdmin || isDN;
  
  const isVisor = role === 'VISOR';

  return {
    'users.view': isAdminOrHigher,
    'users.create': isAdminOrHigher,
    'users.edit': isAdminOrHigher,
    'users.assign_roles': isSuperAdmin || isAdmin,
    'users.assign_scope': isSuperAdmin || isAdmin,

    'exports.pdf': true,
    'exports.instance_data': !isVisor,
    'exports.full_excel': isAdminOrHigher,
    'exports.full_backup': isSuperAdmin,

    'sync.view': isAdminOrHigher,
    'sync.execute': isSuperAdmin || isAdmin,

    'settings.manage': isSuperAdmin || isAdmin,
    'recovery.manage': isSuperAdmin
  };
}

export function hasCapability(rawRole: string | null | undefined, capability: keyof RBACCapabilities | LegacyCapability): boolean {
  const canonicalRole = normalizeRole(rawRole);
  const caps = getRoleCapabilities(canonicalRole);
  
  if (capability === 'canManageUsers') return caps['users.view'];
  if (capability === 'canEditConfig') return caps['settings.manage'];
  if (capability === 'canDownloadReports') return caps['exports.instance_data'];
  if (capability === 'canUploadEvidence') return canonicalRole !== 'VISOR';
  if (capability === 'canValidateEvidence') return ['SUPER_ADMIN', 'ADMINISTRADOR', 'DIRECTOR_NACIONAL', 'VICEPRESIDENTE', 'COORDINADOR', 'PRESIDENTE'].includes(canonicalRole);
  if (capability === 'canDeleteRecords') return canonicalRole === 'SUPER_ADMIN';
  if (capability === 'canViewAllInstances') return ['SUPER_ADMIN', 'ADMINISTRADOR', 'DIRECTOR_NACIONAL'].includes(canonicalRole);
  if (capability === 'canManageOwnInstance') return ['PRESIDENTE', 'COORDINADOR', 'VICEPRESIDENTE'].includes(canonicalRole);

  return caps[capability as keyof RBACCapabilities] || false;
}

export function hasAnyCapability(rawRole: string | null | undefined, capabilities: (keyof RBACCapabilities)[]): boolean {
  return capabilities.some(cap => hasCapability(rawRole, cap));
}

export function hasAllCapabilities(rawRole: string | null | undefined, capabilities: (keyof RBACCapabilities)[]): boolean {
  return capabilities.every(cap => hasCapability(rawRole, cap));
}
